import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useClientLoyalty } from '../../loyalty/queries'
import { usePushNotificationToken } from '../../notification/push-notification-context'
import {
  fetchActiveStations,
  fetchActiveStation,
  fetchStations,
  fetchClientStations,
  fetchStationClient,
} from './api'
import { useStationClientId } from './utils'

import type { QueryContextFromKeys } from '../../utils/react-query'
import type { TerminalType } from './paired.schema'

const stationQueryKeys = {
  all: [{ entity: 'station' }] as const,
  stationClients: () =>
    [{ ...stationQueryKeys.all[0], scope: 'stationClient' }] as const,
  stationClient: (stationClientId?: string) =>
    [
      {
        ...stationQueryKeys.stationClients()[0],
        scope: 'stationClient',
        id: stationClientId,
      },
    ] as const,
}
function useStationClient() {
  const stationClientId = useStationClientId()

  return useQuery({
    queryKey: stationQueryKeys.stationClient(stationClientId),
    queryFn: () => fetchStationClient(stationClientId),
    enabled: Boolean(stationClientId),
  })
}

function usePairedLoyalty() {
  const loyalty = useClientLoyalty()
  const stationClient = useStationClient()

  if (loyalty.status === 'error' || stationClient.status === 'error')
    return {
      status: 'error',
      loyaltyError: loyalty.error,
      stationClientError: stationClient.error,
    } as const

  if (loyalty.status === 'loading' || stationClient.isInitialLoading)
    return { status: 'loading' } as const

  return {
    loyalty: {
      ...loyalty.data,
      checkedInPhone: stationClient.data?.stationClient.e164 ?? null,
      checkedInEmail: stationClient.data?.stationClient.email ?? null,
    } as const,
  }
}

type CheckInQueryKeys = QueryContextFromKeys<typeof checkInQueryKeys>
const checkInQueryKeys = {
  all: [{ entity: 'checkIn' }],
  stations: () => [{ ...checkInQueryKeys.all[0], scope: 'stations' }],
  list: () => [{ ...checkInQueryKeys.stations()[0], list: 'all' }],
  activeStations: () => [
    { ...checkInQueryKeys.stations()[0], status: 'active' },
  ],
  station: ({ type }: { type: TerminalType }) => [
    { ...checkInQueryKeys.stations()[0], type },
  ],
  clientStations: () => [
    { ...checkInQueryKeys.stations()[0], list: 'check-ins' },
  ],
  clientStationsById: (stationId?: string) => [
    {
      ...checkInQueryKeys.clientStations()[0],
      stationId,
    },
  ],
}

function useActiveStations(enabled?: boolean) {
  const { pushToken: deviceToken } = usePushNotificationToken()

  return useQuery({
    queryKey: checkInQueryKeys.activeStations(),
    queryFn: () => fetchActiveStations({ deviceToken }),
    select: (data) => data.data,
    enabled: enabled ?? Boolean(deviceToken),
  })
}

function usePastCheckIns() {
  return useInfiniteQuery({
    queryKey: checkInQueryKeys.clientStationsById(),
    queryFn: fetchClientStations,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
  })
}

// list of check-ins for waiting room page
function useClientStations() {
  const { data: station } = useMyTerminal('pos')

  return useInfiniteQuery({
    queryKey: checkInQueryKeys.clientStationsById(station?.objectId),
    queryFn: fetchClientStations,
    enabled: Boolean(station?.objectId),
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
  })
}

function useMyTerminal(type: TerminalType) {
  const { pushToken: deviceToken } = usePushNotificationToken()

  return useQuery({
    queryKey: checkInQueryKeys.station({ type }),
    queryFn: () => fetchActiveStation({ deviceToken, type }),
    enabled: Boolean(deviceToken),
  })
}

function useTerminals() {
  return useQuery({
    queryKey: checkInQueryKeys.list(),
    queryFn: fetchStations,
  })
}

export type { CheckInQueryKeys }
export {
  checkInQueryKeys,
  useActiveStations,
  useClientStations,
  useMyTerminal,
  usePairedLoyalty,
  usePastCheckIns,
  useTerminals,
}
